import {
  AddShoppingCart,
  AssignmentReturnOutlined,
  CreditCardRounded,
  InfoOutlined,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Card,
  Divider,
  FormControl,
  IconButton,
  MenuItem,
  Popover,
  Select,
  Snackbar,
  Stack,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import { amazonColors } from "../../../config/amazonTheme";
import { useExchangeRate } from "../../../hooks/useExchangeRate";
import { convertUsdToTjs } from "../../../services/exchangeRateService";
import { useCartStore } from "../../../store/useCartStore";
import { Offer, ProductDetailsResponse } from "../../../types/products";
import { ShippingCalculationResponse } from "../../../types/shipping";
import {
  formatPrice,
  formatTjs,
  normalizeBackendPrice,
  normalizeRegularPriceWithContext,
} from "../../../utils/priceUtils";

interface ProductPurchaseBoxProps {
  product: ProductDetailsResponse;
  quantity: number;
  onQuantityChange: (quantity: number) => void;
  onBuyNow: () => void;
  shippingData?: ShippingCalculationResponse | null;
  isPhoneLocked?: boolean;
  isAppleCareSelected?: boolean;
  isRefreshing?: boolean;
  activeOffer?: Offer | null;
  buyBoxOffer?: Offer | null;
  allOffers?: Offer[];
  onSelectOffer?: (offer: Offer | null) => void;
  variant?: "default" | "compact";
  showActions?: boolean;
}

interface ProductPurchaseActionsProps {
  product: ProductDetailsResponse;
  quantity: number;
  onBuyNow: () => void;
  shippingData?: ShippingCalculationResponse | null;
  isPhoneLocked?: boolean;
  isAppleCareSelected?: boolean;
  isRefreshing?: boolean;
  activeOffer?: Offer | null;
  layout?: "row" | "column";
}

const getNormalizedPrices = (product: ProductDetailsResponse) => {
  const displayOriginal = normalizeBackendPrice(
    product.original_retail_price,
    "list",
  );
  const displayPrice = normalizeRegularPriceWithContext(
    product.price,
    displayOriginal,
  );
  return { displayOriginal, displayPrice };
};

const I18N_LOCALE_MAP: Record<string, string> = {
  en: "en-US",
  ru: "ru-RU",
  tg: "tg-TJ",
  uz: "uz-UZ",
};

const getDeliveryDate = (
  deliveryDays?: { min?: number; max?: number },
  lang = "en",
): string | null => {
  if (!deliveryDays?.max) return null;
  const date = new Date();
  date.setDate(date.getDate() + deliveryDays.max);
  const locale = I18N_LOCALE_MAP[lang] || "en-US";
  return date.toLocaleDateString(locale, {
    weekday: "long",
    month: "long",
    day: "numeric",
  });
};

const areOffersSame = (left?: Offer | null, right?: Offer | null): boolean => {
  if (!left || !right) return false;
  if (left === right) return true;
  if (left.offer_id && right.offer_id) {
    return left.offer_id === right.offer_id;
  }
  if (
    left.seller?.id &&
    right.seller?.id &&
    left.price != null &&
    right.price != null
  ) {
    return left.seller.id === right.seller.id && left.price === right.price;
  }
  return false;
};

const extractDeliveryDateText = (greytext?: string | null): string | null => {
  if (!greytext) return null;
  const normalized = greytext.replace(/\s+/g, " ").trim();
  const deliveryMatch = normalized.match(
    /(?:prime members get\s+)?(?:free|fastest)\s+delivery\s+(.+?)(?:\.\s| on orders| order within| join prime|$)/i,
  );

  return (
    deliveryMatch?.[1]?.trim().replace(/\.$/, "") ||
    normalized.replace(/^or\s+/i, "").replace(/\.$/, "")
  );
};

const getOfferDeliveryText = (
  offer?: Offer | null,
  lang = "en",
): string | null => {
  if (!offer) return null;

  const shippingOption = offer.shipping_options?.[0];
  const greytext =
    offer.optimistic?.greytext ||
    offer.greytext ||
    shippingOption?.greytext ||
    null;

  if (greytext) {
    return extractDeliveryDateText(greytext);
  }

  return getDeliveryDate(
    shippingOption?.delivery_days ??
      offer.optimistic?.handling_days ??
      offer.handling_days,
    lang,
  );
};

const getAvailabilityTone = (label?: string | null) => {
  if (!label) return null;
  const normalized = label.toLowerCase();
  if (
    normalized.includes("low") ||
    normalized.includes("few") ||
    normalized.includes("limited") ||
    normalized.includes("unavailable") ||
    normalized.includes("out of stock")
  ) {
    return "low";
  }
  if (normalized.includes("in stock") || normalized.includes("available")) {
    return "ok";
  }
  return "neutral";
};

const ProductPurchaseBox: React.FC<ProductPurchaseBoxProps> = ({
  product,
  quantity,
  onQuantityChange,
  onBuyNow,
  shippingData,
  isPhoneLocked = false,
  isAppleCareSelected = false,
  isRefreshing = false,
  activeOffer = null,
  buyBoxOffer = null,
  allOffers = [],
  onSelectOffer,
  variant = "default",
  showActions = true,
}) => {
  const { t, i18n } = useTranslation();
  const { addToCart } = useCartStore();
  const [showAllSellers, setShowAllSellers] = useState(false);
  const [showAddedMessage, setShowAddedMessage] = React.useState(false);
  const isCompact = variant === "compact";
  const showFooter = !isCompact;

  // Context-aware normalization to handle ambiguous 4-digit integers like 1511 => 15.11
  const { displayOriginal, displayPrice: detailsPrice } =
    getNormalizedPrices(product);

  // Use active offer price (accurate) if available, fallback to details price
  const offersPrice =
    activeOffer?.price != null ? activeOffer.price / 100 : null;
  const displayPrice = offersPrice ?? detailsPrice;
  const hasOfferContext =
    activeOffer !== null ||
    buyBoxOffer !== null ||
    (allOffers?.length ?? 0) > 0;
  const shouldShowPriceUnavailable = displayPrice === null && hasOfferContext;

  // Delivery info from active offer (selected or buy box winner)
  const mainShipping = activeOffer?.shipping_options?.[0];
  const primeGreytext = activeOffer?.optimistic?.greytext || null;
  const deliveryGreytext =
    primeGreytext || activeOffer?.greytext || mainShipping?.greytext || null;
  const deliveryDate = deliveryGreytext
    ? null
    : getDeliveryDate(
        mainShipping?.delivery_days ??
          activeOffer?.optimistic?.handling_days ??
          activeOffer?.handling_days,
        i18n.language,
      );
  const isFreeShipping =
    mainShipping?.price != null && mainShipping.price === 0;
  // Offer's internal shipping fee (what the seller charges within the US)
  const offerShippingFee =
    mainShipping?.price != null ? mainShipping.price / 100 : 0;
  const sellerName = activeOffer?.seller?.name;
  const isFulfilledByMarketplace = activeOffer?.marketplace_fulfilled;

  // TJS conversion
  const { data: exchangeRate } = useExchangeRate();
  const tjsAmount =
    displayPrice !== null
      ? convertUsdToTjs(displayPrice, exchangeRate ?? null)
      : null;
  const availabilityLabel = product.stock_status || product.availability;
  const availabilityTone = getAvailabilityTone(availabilityLabel);

  const handleAddToCart = () => {
    // Pass our cargo shipping cost (US→TJ) if available
    const baseShipping = shippingData?.shippingCost;
    const finalShippingCost =
      baseShipping !== undefined ? baseShipping : undefined;
    // Pass product price + offer's internal shipping fee separately
    // Cart total = product price + offer shipping + cargo shipping (US→TJ)
    addToCart(
      product,
      quantity,
      finalShippingCost,
      offersPrice ?? undefined,
      offerShippingFee || undefined,
      {
        sellerName: activeOffer?.seller?.name,
        sellerId: activeOffer?.seller?.id,
        offerId: activeOffer?.offer_id,
      },
    );
    setShowAddedMessage(true);
  };

  const handleCloseMessage = () => {
    setShowAddedMessage(false);
  };

  const getPriceParts = (priceValue: number) => {
    const formatted = formatPrice(priceValue);
    const match = formatted.match(/^(\D*)(-?\d+)\.(\d{2})$/);
    if (match) {
      return { symbol: match[1] || "$", dollars: match[2], cents: match[3] };
    }
    const asFixed = priceValue.toFixed(2);
    const [dollars, cents] = asFixed.split(".");
    return { symbol: "$", dollars, cents };
  };

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const open = Boolean(anchorEl);

  const formatRetailer = (retailer?: string | null) => {
    if (!retailer) return null;
    const lower = retailer.toLowerCase();
    if (lower.includes("amazon")) return "Amazon";
    if (lower.includes("walmart")) return "Walmart";
    return retailer.charAt(0).toUpperCase() + retailer.slice(1);
  };

  const handleInfoClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleInfoClose = () => {
    setAnchorEl(null);
  };

  const retailerLabel = formatRetailer(product.retailer);
  const showOriginalPrice =
    typeof displayOriginal === "number" &&
    typeof displayPrice === "number" &&
    displayOriginal > displayPrice;
  const rowStyles = {
    display: "grid",
    gridTemplateColumns: "auto 1fr auto",
    alignItems: "center",
    gap: 1,
  };
  const dottedStyles = {
    borderBottom: "1px dashed #e6dec5",
    height: 1,
    mx: 1,
  };

  return (
    <Card
      sx={{
        p: isCompact ? 2.25 : 3,
        boxShadow: "0px 16px 40px rgba(0,0,0,0.06)",
        border: "1px solid #f0e7d8",
        borderRadius: 3,
        backgroundColor: "#fff",
      }}
    >
      <Stack spacing={isCompact ? 1.75 : 2.5}>
        {/* Price */}
        <Box>
          {displayPrice !== null ? (
            (() => {
              const parts = getPriceParts(displayPrice);
              const secureBadge = (
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={0.5}
                  sx={{
                    px: isCompact ? 0.75 : 1,
                    py: isCompact ? 0.2 : 0.25,
                    borderRadius: 999,
                    bgcolor: "rgba(15, 118, 110, 0.12)",
                    color: "#0f766e",
                    fontSize: isCompact ? "0.65rem" : "0.7rem",
                    fontWeight: 700,
                  }}
                >
                  <CreditCardRounded sx={{ fontSize: isCompact ? 12 : 14 }} />
                  <Box component="span">Secure</Box>
                </Stack>
              );

              // TJS-first: show TJS as primary bold price, USD as secondary
              const primaryPriceLine =
                tjsAmount !== null ? (
                  <Box
                    sx={{ display: "flex", alignItems: "baseline", gap: 0.5 }}
                  >
                    <Typography
                      component="span"
                      sx={{
                        fontWeight: 700,
                        fontSize: isCompact ? "1.6rem" : "2rem",
                        lineHeight: 1,
                      }}
                    >
                      {formatTjs(tjsAmount)}
                    </Typography>
                    <Typography
                      component="span"
                      sx={{
                        fontWeight: 600,
                        fontSize: isCompact ? "0.9rem" : "1rem",
                      }}
                    >
                      c.
                    </Typography>
                  </Box>
                ) : (
                  <Box
                    sx={{ display: "flex", alignItems: "flex-start", gap: 0.5 }}
                  >
                    <Typography
                      component="span"
                      sx={{ fontWeight: 500, fontSize: "1rem" }}
                    >
                      {parts.symbol}
                    </Typography>
                    <Typography
                      component="span"
                      sx={{
                        fontWeight: 700,
                        fontSize: isCompact ? "1.6rem" : "2rem",
                        lineHeight: 1,
                      }}
                    >
                      {parts.dollars}
                    </Typography>
                    <Typography
                      component="span"
                      sx={{
                        fontWeight: 500,
                        fontSize: isCompact ? "0.9rem" : "1rem",
                      }}
                    >
                      {parts.cents}
                    </Typography>
                  </Box>
                );

              return (
                <Box
                  translate="no"
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    columnGap: 1,
                    width: "100%",
                  }}
                >
                  {primaryPriceLine}
                  <Box sx={{ ml: "auto", display: "flex" }}>{secureBadge}</Box>
                </Box>
              );
            })()
          ) : shouldShowPriceUnavailable ? (
            <Typography
              variant="body2"
              sx={{ color: amazonColors.text.secondary }}
              className="notranslate"
              translate="no"
            >
              {t("products.priceNotAvailable")}
            </Typography>
          ) : null}
          {/* USD shown as secondary when TJS is available */}
          {tjsAmount !== null && displayPrice !== null && (
            <Typography
              variant="body2"
              className="notranslate"
              translate="no"
              sx={{
                color: "#888",
                fontWeight: 400,
                fontSize: isCompact ? "0.8rem" : "0.85rem",
                mt: 0.25,
              }}
            >
              ({formatPrice(displayPrice)})
            </Typography>
          )}
          {showOriginalPrice && displayOriginal && (
            <Typography
              variant="caption"
              sx={{
                color: amazonColors.text.secondary,
                textDecoration: "line-through",
              }}
            >
              {formatPrice(displayOriginal)}
            </Typography>
          )}
        </Box>

        {/* Availability - show below price for all tones */}
        {availabilityLabel && availabilityTone && availabilityTone !== "ok" && (
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              px: 1,
              py: 0.4,
              borderRadius: 999,
              bgcolor:
                availabilityTone === "low"
                  ? "rgba(220, 38, 38, 0.12)"
                  : "rgba(107, 114, 128, 0.12)",
              color:
                availabilityTone === "low"
                  ? amazonColors.red
                  : amazonColors.text.secondary,
              fontWeight: 700,
              fontSize: "0.75rem",
              width: "fit-content",
            }}
          >
            {availabilityLabel}
          </Box>
        )}

        {/* Delivery & Shipping info from scraper */}
        {product.delivery && product.delivery.length > 0 ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            {product.delivery.map((item, index) => {
              let dateStr = item.date;
              try {
                if (dateStr) {
                  const parsed = JSON.parse(dateStr);
                  if (parsed.by) dateStr = parsed.by;
                }
              } catch (e) {
                // Fallback to original string if not valid JSON
              }

              const isFree = item.type?.toLowerCase().includes("free");

              return (
                <Typography
                  key={index}
                  variant="body2"
                  sx={{ color: amazonColors.text.primary, fontWeight: 500 }}
                >
                  {t("products.usaDelivery")}:{" "}
                  {isFree && isFreeShipping ? (
                    <Box
                      component="span"
                      sx={{ color: amazonColors.green, fontWeight: 700 }}
                    >
                      {t("products.free")}
                    </Box>
                  ) : offerShippingFee > 0 ? (
                    <Box
                      component="span"
                      sx={{ color: amazonColors.text.secondary }}
                    >
                      {formatPrice(offerShippingFee)}
                    </Box>
                  ) : (
                    item.type
                  )}
                  {", "}
                  {dateStr}
                </Typography>
              );
            })}
          </Box>
        ) : product.delivery_messages && product.delivery_messages.length > 0 && !activeOffer && !buyBoxOffer && (allOffers?.length ?? 0) === 0 ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            {product.delivery_messages.map((msg, index) => (
              <Typography
                key={index}
                variant="body2"
                className="notranslate"
                translate="no"
                sx={{ color: amazonColors.text.primary, fontWeight: 500 }}
              >
                {msg.replace(/\.\s*Details$/i, "").trim()}
              </Typography>
            ))}
          </Box>
        ) : (
          /* Fallback to existing delivery logic */
          activeOffer &&
          (deliveryGreytext || deliveryDate) && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
              <Typography
                variant="body2"
                sx={{ color: amazonColors.text.primary, fontWeight: 500 }}
              >
                {deliveryGreytext ? (
                  (() => {
                    // Greytext can be multiline with multiple delivery options.
                    // Prefer Prime line (earliest free delivery), else first free line.
                    const lines = deliveryGreytext
                      .split("\n")
                      .map((l) => l.trim())
                      .filter(Boolean);
                    const primeLine = lines.find((l) =>
                      l.toLowerCase().includes("prime"),
                    );
                    const freeLine = lines.find((l) =>
                      l.toLowerCase().includes("free"),
                    );
                    const targetLine = primeLine || freeLine || lines[0];
                    // Extract just the date (e.g. "Tomorrow, May 22" or "Monday, May 25")
                    const datePatternMatch = targetLine.match(
                      /\b(Tomorrow|Today|Monday|Tuesday|Wednesday|Thursday|Friday|Saturday|Sunday),?\s+\w+\.?\s+\d+/i,
                    );
                    const dateText = datePatternMatch
                      ? datePatternMatch[0].trim()
                      : (() => {
                          const m = targetLine.match(
                            /(?:FREE|fastest)\s+delivery\s+(.+?)(?:\.|$)/i,
                          );
                          return m
                            ? m[1]
                                .replace(/\s+on orders.*/i, "")
                                .replace(/\s+Order within.*/i, "")
                                .trim()
                            : targetLine;
                        })();
                    return (
                      <>
                        {t("products.usaDelivery")}:{" "}
                        {isFreeShipping ? (
                          <Box
                            component="span"
                            sx={{ color: amazonColors.green, fontWeight: 700 }}
                          >
                            {t("products.free")}
                          </Box>
                        ) : offerShippingFee > 0 ? (
                          <Box
                            component="span"
                            sx={{ color: amazonColors.text.secondary }}
                          >
                            {formatPrice(offerShippingFee)}
                          </Box>
                        ) : null}
                        {", "}
                        {dateText}
                      </>
                    );
                  })()
                ) : isFreeShipping ? (
                  <>
                    {t("products.usaDelivery")}:{" "}
                    <Box
                      component="span"
                      sx={{ color: amazonColors.green, fontWeight: 700 }}
                    >
                      {t("products.free")}
                    </Box>
                    {", "}
                    {deliveryDate}
                  </>
                ) : (
                  <>
                    {t("products.usaDelivery")}: {deliveryDate}
                    {mainShipping?.price != null && (
                      <Box
                        component="span"
                        sx={{ color: amazonColors.text.secondary }}
                      >
                        {" "}
                        · {formatPrice(mainShipping.price / 100)}
                      </Box>
                    )}
                  </>
                )}
              </Typography>
            </Box>
          )
        )}

        {/* Quantity */}
        <FormControl fullWidth>
          <Select
            value={quantity}
            onChange={(e) => onQuantityChange(e.target.value as number)}
            size="small"
            disabled={displayPrice === null}
            renderValue={(selected) =>
              t("products.quantity", { quantity: selected as number })
            }
            sx={{
              backgroundColor: "#fff",
              borderRadius: 2,
              "& .MuiSelect-select": {
                display: "flex",
                alignItems: "center",
                py: 0.75,
              },
            }}
          >
            {(() => {
              // Default max quantity of 10
              const maxQuantity = 20;
              const quantities = [] as number[];
              for (let i = 1; i <= maxQuantity; i++) {
                quantities.push(i);
              }
              return quantities.map((num) => (
                <MenuItem key={num} value={num}>
                  {num}
                </MenuItem>
              ));
            })()}
          </Select>
        </FormControl>

        {/* Action Buttons */}
        {showActions && availabilityTone !== "low" && (
          <Stack spacing={isCompact ? 1.5 : 2}>
            <Button
              variant="contained"
              size={isCompact ? "medium" : "large"}
              startIcon={<AddShoppingCart />}
              onClick={handleAddToCart}
              disabled={
                displayPrice === null ||
                isPhoneLocked ||
                isAppleCareSelected ||
                isRefreshing
              }
              sx={{
                backgroundColor:
                  isPhoneLocked || isAppleCareSelected
                    ? amazonColors.gray[300]
                    : amazonColors.yellow,
                color:
                  isPhoneLocked || isAppleCareSelected
                    ? amazonColors.gray[500]
                    : amazonColors.text.primary,
                fontWeight: 700,
                fontSize: "1rem",
                py: isCompact ? 1.2 : 1.5,
                border: `1px solid ${
                  isPhoneLocked || isAppleCareSelected
                    ? amazonColors.gray[300]
                    : amazonColors.yellow
                }`,
                "&:hover": {
                  backgroundColor:
                    isPhoneLocked || isAppleCareSelected
                      ? amazonColors.gray[300]
                      : amazonColors.yellowHover,
                  borderColor:
                    isPhoneLocked || isAppleCareSelected
                      ? amazonColors.gray[300]
                      : amazonColors.yellowHover,
                },
              }}
            >
              <span className="notranslate" translate="no">
                {t("products.addToCart")}
              </span>
            </Button>
            <Button
              variant="contained"
              size={isCompact ? "medium" : "large"}
              onClick={onBuyNow}
              disabled={
                displayPrice === null ||
                isPhoneLocked ||
                isAppleCareSelected ||
                isRefreshing
              }
              sx={{
                backgroundColor:
                  isPhoneLocked || isAppleCareSelected
                    ? amazonColors.gray[300]
                    : amazonColors.orange,
                color:
                  isPhoneLocked || isAppleCareSelected
                    ? amazonColors.gray[500]
                    : "white",
                fontWeight: 700,
                fontSize: "1rem",
                py: isCompact ? 1.2 : 1.5,
                border: `1px solid ${
                  isPhoneLocked || isAppleCareSelected
                    ? amazonColors.gray[300]
                    : amazonColors.orange
                }`,
                "&:hover": {
                  backgroundColor:
                    isPhoneLocked || isAppleCareSelected
                      ? amazonColors.gray[300]
                      : amazonColors.orangeHover,
                  borderColor:
                    isPhoneLocked || isAppleCareSelected
                      ? amazonColors.gray[300]
                      : amazonColors.orangeHover,
                },
              }}
            >
              <span className="notranslate" translate="no">
                {t("products.buyNow")}
              </span>
            </Button>
          </Stack>
        )}

        {/* Friday flight reservation removed */}

        {/* All Sellers — with condition on each card, expandable */}
        {allOffers && allOffers.length > 1 && (
          <Divider sx={{ borderStyle: "dashed", borderColor: "#e9dec0" }} />
        )}
        {allOffers &&
          allOffers.length > 1 &&
          (() => {
            const INITIAL_SHOW = 4;
            const totalOffers = allOffers.length;
            const visibleOffers = showAllSellers
              ? allOffers
              : allOffers.slice(0, INITIAL_SHOW);

            return (
              <Box sx={{ mt: 1 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 0.5,
                  }}
                >
                  <Typography
                    variant="caption"
                    className="notranslate"
                    translate="no"
                    sx={{
                      fontWeight: 700,
                      color: amazonColors.text.primary,
                      fontSize: "0.8rem",
                    }}
                  >
                    {t("products.sellersOnRetailer", {
                      retailer: product.retailer
                        ? product.retailer.charAt(0).toUpperCase() +
                          product.retailer.slice(1)
                        : "Amazon",
                    })}
                  </Typography>
                  {totalOffers > INITIAL_SHOW && (
                    <Typography
                      variant="caption"
                      className="notranslate"
                      translate="no"
                      sx={{ color: amazonColors.text.secondary }}
                    >
                      {totalOffers} {t("products.sellersCount")}
                    </Typography>
                  )}
                </Box>
                <Typography
                  variant="caption"
                  className="notranslate"
                  translate="no"
                  sx={{
                    color: amazonColors.text.secondary,
                    display: "block",
                    mb: 1,
                  }}
                >
                  {t("products.sortedByPrice")}
                </Typography>

                <Stack spacing={0.5}>
                  {visibleOffers.map((offer, idx) => {
                    const isSelected = areOffersSame(offer, activeOffer);
                    const offerShipping = offer.shipping_options?.[0];
                    const offerFreeShip =
                      offerShipping?.price != null && offerShipping.price === 0;
                    const condition = offer.condition || "New";
                    const offerDeliveryText = getOfferDeliveryText(
                      offer,
                      i18n.language,
                    );
                    return (
                      <Box
                        key={offer.offer_id || `${offer.seller?.id}-${idx}`}
                        onClick={() =>
                          onSelectOffer?.(
                            areOffersSame(offer, buyBoxOffer) ? null : offer,
                          )
                        }
                        sx={{
                          p: 1,
                          borderRadius: 1.5,
                          border: isSelected
                            ? "2px solid #f59e0b"
                            : "1px solid #f0e7d8",
                          backgroundColor: isSelected ? "#fffbeb" : "#fffdf8",
                          cursor: "pointer",
                          transition:
                            "border-color 0.15s, background-color 0.15s",
                          "&:hover": {
                            borderColor: isSelected ? "#f59e0b" : "#e0d5b8",
                            backgroundColor: isSelected ? "#fffbeb" : "#fff8f0",
                          },
                        }}
                      >
                        {/* Row 1: Condition */}
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 700,
                            color: amazonColors.text.primary,
                            fontSize: "0.72rem",
                          }}
                        >
                          {condition}
                        </Typography>
                        {/* Row 2: Price + Shipping + Best Offer */}
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.75,
                            mt: 0.25,
                            flexWrap: "wrap",
                          }}
                          className="notranslate"
                          translate="no"
                        >
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            {formatPrice(
                              offer.price != null ? offer.price / 100 : null,
                            )}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              color: offerFreeShip
                                ? amazonColors.green
                                : amazonColors.text.secondary,
                              fontWeight: offerFreeShip ? 600 : 400,
                              fontSize: "0.7rem",
                            }}
                          >
                            {offerFreeShip
                              ? t("products.freeShipping")
                              : offerShipping?.price != null
                                ? `+${formatPrice(offerShipping.price / 100)} ${t("products.shippingLabel")}`
                                : ""}
                          </Typography>
                        </Box>
                        {offerDeliveryText && (
                          <Typography
                            variant="caption"
                            className="notranslate"
                            translate="no"
                            sx={{
                              color: amazonColors.text.secondary,
                              display: "block",
                              mt: 0.15,
                              fontSize: "0.68rem",
                              lineHeight: 1.25,
                            }}
                          >
                            {offerDeliveryText}
                          </Typography>
                        )}
                        {/* Row 4: Seller name */}
                        <Typography
                          variant="caption"
                          className="notranslate"
                          translate="no"
                          sx={{
                            color: amazonColors.text.secondary,
                            display: "block",
                            mt: 0.25,
                          }}
                        >
                          {offer.seller?.name || t("products.unknownSeller")}
                        </Typography>
                        {/* Row 5: Rating */}
                        {offer.seller?.percent_positive != null && (
                          <Typography
                            variant="caption"
                            className="notranslate"
                            translate="no"
                            sx={{
                              color: amazonColors.text.secondary,
                              display: "flex",
                              alignItems: "center",
                              gap: 0.25,
                              fontSize: "0.7rem",
                            }}
                          >
                            {"★"}{" "}
                            {t("products.sellerRating", {
                              percent: offer.seller.percent_positive,
                            })}
                            {offer.seller.num_ratings != null && (
                              <Box component="span" sx={{ opacity: 0.7 }}>
                                ({offer.seller.num_ratings.toLocaleString()})
                              </Box>
                            )}
                          </Typography>
                        )}
                      </Box>
                    );
                  })}
                </Stack>

                {totalOffers > INITIAL_SHOW && (
                  <Button
                    size="small"
                    className="notranslate"
                    translate="no"
                    onClick={() => setShowAllSellers((prev) => !prev)}
                    sx={{
                      mt: 1,
                      width: "100%",
                      textTransform: "none",
                      color: amazonColors.blue,
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      py: 0.75,
                      borderRadius: 2,
                      border: "1px solid #f0e7d8",
                      "&:hover": {
                        backgroundColor: "rgba(255, 189, 63, 0.08)",
                        borderColor: "#e0d5b8",
                      },
                    }}
                  >
                    {showAllSellers
                      ? t("products.showLess")
                      : t("products.showAllSellers", {
                          count: totalOffers,
                        })}
                  </Button>
                )}
              </Box>
            );
          })()}
      </Stack>

      {/* Return Policy */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          mt: isCompact ? 1.25 : 1.5,
          mb: 1,
          px: isCompact ? 1 : 1.25,
          py: isCompact ? 0.75 : 1,
          borderRadius: 2,
          border: "1px solid #f0e7d8",
          backgroundColor: "#fffaf2",
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: "50%",
              backgroundColor: "rgba(255, 189, 63, 0.18)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <AssignmentReturnOutlined
              sx={{ fontSize: 16, color: amazonColors.orange }}
            />
          </Box>
          <Typography
            variant="body2"
            sx={{ color: amazonColors.text.primary, fontWeight: 700 }}
            className="notranslate"
            translate="no"
          >
            {t("products.returnPolicy")}
          </Typography>
        </Stack>
        <IconButton
          size="small"
          onClick={handleInfoClick}
          sx={{
            p: 0.4,
            bgcolor: "rgba(255, 255, 255, 0.9)",
            border: "1px solid rgba(15, 23, 42, 0.08)",
            "&:hover": { bgcolor: "rgba(255, 255, 255, 1)" },
          }}
        >
          <InfoOutlined sx={{ fontSize: 16, color: amazonColors.orange }} />
        </IconButton>
      </Box>

      {showActions && (
        <Snackbar
          open={showAddedMessage}
          autoHideDuration={3000}
          onClose={handleCloseMessage}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            onClose={handleCloseMessage}
            severity="success"
            sx={{ width: "100%" }}
            className="notranslate"
            translate="no"
          >
            {t("products.itemAddedToCart")}
          </Alert>
        </Snackbar>
      )}

      {/* Return Policy Popover */}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleInfoClose}
        anchorOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        sx={{
          "& .MuiPopover-paper": {
            borderRadius: 2,
            minWidth: 160,
            maxWidth: 180,
            p: 1.5,
          },
        }}
      >
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{}}
          className="notranslate"
          translate="no"
        >
          {t("products.returnPolicyInfo")}
        </Typography>
      </Popover>
    </Card>
  );
};

export const ProductPurchaseActions: React.FC<ProductPurchaseActionsProps> = ({
  product,
  quantity,
  onBuyNow,
  shippingData,
  isPhoneLocked = false,
  isAppleCareSelected = false,
  isRefreshing = false,
  activeOffer = null,
  layout = "row",
}) => {
  const { t } = useTranslation();
  const { addToCart } = useCartStore();
  const [showAddedMessage, setShowAddedMessage] = React.useState(false);
  const { displayPrice: detailsPrice } = getNormalizedPrices(product);
  const offerPrice =
    activeOffer?.price != null ? activeOffer.price / 100 : null;
  const displayPrice = offerPrice ?? detailsPrice;
  const offerShipping = activeOffer?.shipping_options?.[0];
  const offerShippingFee =
    offerShipping?.price != null ? offerShipping.price / 100 : 0;

  const handleAddToCart = () => {
    const baseShipping = shippingData?.shippingCost;
    const finalShippingCost =
      baseShipping !== undefined ? baseShipping : undefined;
    addToCart(
      product,
      quantity,
      finalShippingCost,
      offerPrice ?? undefined,
      offerShippingFee || undefined,
      {
        sellerName: activeOffer?.seller?.name,
        sellerId: activeOffer?.seller?.id,
        offerId: activeOffer?.offer_id,
      },
    );
    setShowAddedMessage(true);
  };

  const handleCloseMessage = () => {
    setShowAddedMessage(false);
  };

  const availabilityLabel = product.stock_status || product.availability;
  const availabilityTone = getAvailabilityTone(availabilityLabel);

  if (availabilityTone === "low") return null;

  const isDisabled =
    displayPrice === null ||
    isPhoneLocked ||
    isAppleCareSelected ||
    isRefreshing;
  const isRow = layout === "row";
  const buttonSx = {
    flex: isRow ? 1 : undefined,
    fontWeight: 700,
    fontSize: "0.95rem",
    py: isRow ? 1.1 : 1.4,
    borderRadius: 2,
    boxShadow: "none",
  };

  return (
    <>
      <Stack direction={layout} spacing={1.2}>
        <Button
          variant="contained"
          size={isRow ? "medium" : "large"}
          startIcon={isRow ? undefined : <AddShoppingCart />}
          onClick={handleAddToCart}
          disabled={isDisabled}
          sx={{
            ...buttonSx,
            backgroundColor: isDisabled
              ? amazonColors.gray[300]
              : amazonColors.yellow,
            color: isDisabled
              ? amazonColors.gray[500]
              : amazonColors.text.primary,
            border: `1px solid ${
              isDisabled ? amazonColors.gray[300] : amazonColors.yellow
            }`,
            "&:hover": {
              backgroundColor: isDisabled
                ? amazonColors.gray[300]
                : amazonColors.yellowHover,
              borderColor: isDisabled
                ? amazonColors.gray[300]
                : amazonColors.yellowHover,
            },
          }}
        >
          <span className="notranslate" translate="no">
            {t("products.addToCart")}
          </span>
        </Button>
        <Button
          variant="contained"
          size={isRow ? "medium" : "large"}
          onClick={onBuyNow}
          disabled={isDisabled}
          sx={{
            ...buttonSx,
            backgroundColor: isDisabled
              ? amazonColors.gray[300]
              : amazonColors.orange,
            color: isDisabled ? amazonColors.gray[500] : "white",
            border: `1px solid ${
              isDisabled ? amazonColors.gray[300] : amazonColors.orange
            }`,
            "&:hover": {
              backgroundColor: isDisabled
                ? amazonColors.gray[300]
                : amazonColors.orangeHover,
              borderColor: isDisabled
                ? amazonColors.gray[300]
                : amazonColors.orangeHover,
            },
          }}
        >
          <span className="notranslate" translate="no">
            {t("products.buyNow")}
          </span>
        </Button>
      </Stack>

      <Snackbar
        open={showAddedMessage}
        autoHideDuration={3000}
        onClose={handleCloseMessage}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseMessage}
          severity="success"
          sx={{ width: "100%" }}
          className="notranslate"
          translate="no"
        >
          {t("products.itemAddedToCart")}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ProductPurchaseBox;
